import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Calendar,
    ChevronLeft,
    CreditCard,
    Crown,
    ExternalLink,
    Gift,
    Image,
    Mail,
    Megaphone,
    Receipt,
    Save,
    ShoppingBag,
    Sparkles
} from 'lucide-react';
import { clubsService } from '../../../services';
import { useUser } from '../../../UserContext';
import { appConfig } from '../../../config/appConfig';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import { VENUE_PLAN_ROWS } from './venueFeatureConfig';
import { getClubPublicPath, getClubStorefrontPath } from '../../../utils/clubPublicPaths';

const CRM_STAGE_OPTIONS = [
    'Prospect',
    'Attempting Contact',
    'Contacted',
    'Interested',
    'Proposal Sent',
    'Negotiating',
    'Won',
    'Lost',
    'On Hold'
];

const CRM_CONTACT_METHODS = ['Phone', 'Email', 'WhatsApp', 'In Person', 'Other'];

const PREMIUM_TOOLKIT = [
    {
        title: 'Maps & Photos',
        description: 'Show off the ground properly with gallery imagery, maps, and richer visual presentation.',
        icon: Image
    },
    {
        title: 'Events',
        description: 'Publish events and sessions so users can see more activity happening at your venue.',
        icon: Calendar
    },
    {
        title: 'Till / POS',
        description: 'Run a simple venue counter till with catalogue, checkout flow, and sales tracking.',
        icon: Receipt
    },
    {
        title: 'Communications',
        description: 'Send updates and targeted messages to venue members, admins, and owners.',
        icon: Megaphone
    },
    {
        title: 'Offers & Perks',
        description: 'Promote current offers, memberships, and venue deals more visibly across the platform.',
        icon: Gift
    },
    {
        title: 'Marketplace / Shopfront',
        description: 'Create a proper venue shopfront and trial items on the marketplace to demonstrate value.',
        icon: ShoppingBag
    }
];
const VenuePlan = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [club, setClub] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isPremiumVenue, setIsPremiumVenue] = useState(false);
    const [crm, setCrm] = useState(null);
    const [crmForm, setCrmForm] = useState({
        stage: 'Prospect',
        nextFollowUpAt: '',
        lastContactedAt: '',
        lastContactMethod: '',
        summary: ''
    });
    const [crmNoteForm, setCrmNoteForm] = useState({
        note: '',
        stage: 'Prospect',
        contactMethod: 'Phone',
        occurredAt: new Date().toISOString().slice(0, 16),
        followUpAt: ''
    });
    const isPlatformAdmin = Boolean(user?.isAdmin);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, nextSubscriptionData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubSubscription(clubId)
            ]);

            setClub(clubData);
            setIsPremiumVenue(Boolean(nextSubscriptionData?.isPremiumVenue || (nextSubscriptionData?.profileTier || '').toLowerCase() === 'premium'));

            if (isPlatformAdmin) {
                const crmData = await clubsService.getClubAdminCrm(clubId);
                setCrm(crmData);
                setCrmForm({
                    stage: crmData?.stage || 'Prospect',
                    nextFollowUpAt: crmData?.nextFollowUpAt ? crmData.nextFollowUpAt.slice(0, 16) : '',
                    lastContactedAt: crmData?.lastContactedAt ? crmData.lastContactedAt.slice(0, 16) : '',
                    lastContactMethod: crmData?.lastContactMethod || '',
                    summary: crmData?.summary || ''
                });
                setCrmNoteForm((current) => ({
                    ...current,
                    stage: crmData?.stage || current.stage
                }));
            }
        } catch (err) {
            setError(err.message || 'Failed to load venue plan details.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId, isPlatformAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    const currentTier = isPremiumVenue ? 'Premium Venue' : 'Free Venue';
    const upgradeMailto = useMemo(() => {
        const subject = encodeURIComponent(`Venue plan upgrade request: ${club?.name || `Venue ${clubId}`}`);
        const body = encodeURIComponent(
            `Hi Shoot Connect,%0D%0A%0D%0APlease upgrade ${club?.name || `venue ${clubId}`} to the Premium Venue plan.%0D%0AWe understand Premium venues are invoiced manually.%0D%0A%0D%0AThanks`
        );
        return `mailto:${appConfig.venuesEmail}?subject=${subject}&body=${body}`;
    }, [club?.name, clubId]);

    const handleAdminSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await clubsService.updateClubSubscription(clubId, {
                isPremiumVenue
            });
            setSuccess('Premium venue access updated successfully.');
            await load();
        } catch (err) {
            setError(err.message || 'Failed to update premium venue access.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveCrm = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await clubsService.updateClubAdminCrm(clubId, {
                stage: crmForm.stage,
                nextFollowUpAt: crmForm.nextFollowUpAt || null,
                lastContactedAt: crmForm.lastContactedAt || null,
                lastContactMethod: crmForm.lastContactMethod || null,
                summary: crmForm.summary || null
            });
            setSuccess('Venue CRM updated successfully.');
            await load();
        } catch (err) {
            setError(err.message || 'Failed to update venue CRM.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddCrmNote = async () => {
        if (!crmNoteForm.note.trim()) {
            setError('Please add a CRM note before saving.');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await clubsService.createClubAdminCrmNote(clubId, {
                note: crmNoteForm.note.trim(),
                stage: crmNoteForm.stage,
                contactMethod: crmNoteForm.contactMethod || null,
                occurredAt: crmNoteForm.occurredAt || null,
                followUpAt: crmNoteForm.followUpAt || null
            });
            setSuccess('CRM note added.');
            setCrmNoteForm((current) => ({
                ...current,
                note: '',
                followUpAt: ''
            }));
            await load();
        } catch (err) {
            setError(err.message || 'Failed to add CRM note.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading venue plan...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-green">
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h1>Venue Plan</h1>
                        <p>Compare Free vs Premium venue features and request an upgrade for {club?.name}</p>
                    </div>
                </div>
            </div>

            {error && <div className="manage-clubs-error">{error}</div>}
            {success && <div className="manage-clubs-error" style={{ background: '#e8f8ef', color: '#12715B' }}>{success}</div>}

            <div className="dashboard-card-container">
                <div className="dashboard-card venue-plan-hero-card">
                    <div className="venue-plan-hero-grid">
                        <div className="venue-plan-hero-copy">
                            <span className={`venue-plan-pill ${isPremiumVenue ? 'premium' : 'free'}`}>
                                {isPremiumVenue ? <Crown size={16} /> : <CreditCard size={16} />}
                                {currentTier}
                            </span>
                            <h2>{isPremiumVenue ? 'Your venue is already running on Premium' : 'Upgrade this venue to Premium'}</h2>
                            <p>
                                {isPremiumVenue
                                    ? 'Your venue has access to the fuller premium toolset, richer public presentation, and the venue shopfront experience.'
                                    : 'Premium gives venues a much stronger presence inside Shoot Connect. We will work with you to get the profile looking great, bring current offers live, and even trial marketplace items so you can see the full experience in action.'}
                            </p>
                            <div className="venue-plan-upgrade-actions">
                                <a
                                    href={upgradeMailto}
                                    className="manage-section-add-btn"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Mail size={16} />
                                    {isPremiumVenue ? 'Contact Us' : 'Upgrade'}
                                </a>
                                <a
                                    href={getClubPublicPath(club)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="manage-section-add-btn manage-section-add-btn-secondary"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <ExternalLink size={16} />
                                    View Public Venue Profile
                                </a>
                                <a
                                    href={getClubStorefrontPath(club)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="manage-section-add-btn manage-section-add-btn-secondary"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <ExternalLink size={16} />
                                    View Venue Shopfront
                                </a>
                            </div>
                        </div>

                        <div className="venue-plan-hero-panel">
                            <div className="venue-plan-hero-panel-head">
                                <Sparkles size={18} />
                                <span>What we help with</span>
                            </div>
                            <ul className="venue-plan-promise-list">
                                <li>We will help shape the venue profile so it looks polished and complete.</li>
                                <li>We can get any current offers or memberships live in Connect Perks.</li>
                                <li>We can trial marketplace listings to demonstrate the shopfront properly.</li>
                                <li>Premium venues are invoiced manually by Shoot Connect after agreement.</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="dashboard-card venue-plan-compare-card">
                    <h2>Free vs Premium</h2>
                    <p>The venue plan controls feature access and public presentation level. Premium venue upgrades are handled directly by Shoot Connect and invoiced manually once agreed.</p>
                    <div className="venue-plan-table-wrapper">
                        <table className="venue-plan-table">
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th>Free Venue</th>
                                    <th>Premium Venue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {VENUE_PLAN_ROWS.map((row) => (
                                    <tr key={row.feature}>
                                        <td>{row.feature}</td>
                                        <td>{row.free}</td>
                                        <td>{row.premium}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="venue-plan-feature-note">
                        Maps & photos, events, till / POS, communications, and marketplace / shopfront are Premium venue features.
                    </div>
                </div>

                <div className="dashboard-card venue-plan-toolkit-card">
                    <div className="venue-plan-panel-heading">
                        <Sparkles size={18} />
                        <h2>Premium Venue Toolkit</h2>
                    </div>
                    <p>The Premium plan is designed to help venues actually demonstrate value inside the platform, not just appear on it.</p>
                    <div className="venue-plan-toolkit-grid">
                        {PREMIUM_TOOLKIT.map((item) => {
                            const Icon = item.icon;
                            return (
                                <article key={item.title} className="venue-plan-toolkit-item">
                                    <div className="venue-plan-toolkit-icon">
                                        <Icon size={18} />
                                    </div>
                                    <strong>{item.title}</strong>
                                    <p>{item.description}</p>
                                </article>
                            );
                        })}
                    </div>
                    {!isPremiumVenue ? (
                        <div className="venue-plan-feature-note">
                            <strong>How upgrade works:</strong> contact us, we agree the Premium setup, and Shoot Connect invoices the venue directly.
                        </div>
                    ) : null}
                </div>

                {isPlatformAdmin ? (
                    <>
                        <div id="shoot-connect-crm" className="dashboard-card venue-plan-admin-card">
                            <div className="venue-plan-panel-heading">
                                <Megaphone size={18} />
                                <h2>Shoot Connect CRM</h2>
                            </div>
                            <p>Track venue outreach, stage, follow-up timing, and timestamped contact notes for internal Shoot Connect staff only.</p>

                            <div className="dashboard-two-column-grid">
                                <div className="venue-plan-feature-panel">
                                    <h3>CRM Status</h3>
                                    <div className="form-group">
                                        <label>Stage</label>
                                        <select value={crmForm.stage} onChange={(event) => setCrmForm((current) => ({ ...current, stage: event.target.value }))}>
                                            {CRM_STAGE_OPTIONS.map((stage) => (
                                                <option key={stage} value={stage}>{stage}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Next Follow-Up</label>
                                        <input
                                            type="datetime-local"
                                            value={crmForm.nextFollowUpAt}
                                            onChange={(event) => setCrmForm((current) => ({ ...current, nextFollowUpAt: event.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Contacted</label>
                                        <input
                                            type="datetime-local"
                                            value={crmForm.lastContactedAt}
                                            onChange={(event) => setCrmForm((current) => ({ ...current, lastContactedAt: event.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Contact Method</label>
                                        <select value={crmForm.lastContactMethod} onChange={(event) => setCrmForm((current) => ({ ...current, lastContactMethod: event.target.value }))}>
                                            <option value="">Select method</option>
                                            {CRM_CONTACT_METHODS.map((method) => (
                                                <option key={method} value={method}>{method}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Summary</label>
                                        <textarea
                                            rows={4}
                                            value={crmForm.summary}
                                            onChange={(event) => setCrmForm((current) => ({ ...current, summary: event.target.value }))}
                                            placeholder="Quick summary of the venue's current commercial position"
                                        />
                                    </div>
                                    <div className="venue-plan-upgrade-actions">
                                        <button type="button" className="manage-section-add-btn" onClick={handleSaveCrm} disabled={isSaving}>
                                            <Save size={16} />
                                            {isSaving ? 'Saving...' : 'Save CRM'}
                                        </button>
                                    </div>
                                </div>

                                <div className="venue-plan-feature-panel">
                                    <h3>Add CRM Note</h3>
                                    <div className="form-group">
                                        <label>Note</label>
                                        <textarea
                                            rows={5}
                                            value={crmNoteForm.note}
                                            onChange={(event) => setCrmNoteForm((current) => ({ ...current, note: event.target.value }))}
                                            placeholder="Called venue manager, sent proposal, waiting on reply, offered trial month, etc."
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Stage At Time Of Note</label>
                                        <select value={crmNoteForm.stage} onChange={(event) => setCrmNoteForm((current) => ({ ...current, stage: event.target.value }))}>
                                            {CRM_STAGE_OPTIONS.map((stage) => (
                                                <option key={stage} value={stage}>{stage}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Method</label>
                                        <select value={crmNoteForm.contactMethod} onChange={(event) => setCrmNoteForm((current) => ({ ...current, contactMethod: event.target.value }))}>
                                            {CRM_CONTACT_METHODS.map((method) => (
                                                <option key={method} value={method}>{method}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Occurred At</label>
                                        <input
                                            type="datetime-local"
                                            value={crmNoteForm.occurredAt}
                                            onChange={(event) => setCrmNoteForm((current) => ({ ...current, occurredAt: event.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Set Next Follow-Up</label>
                                        <input
                                            type="datetime-local"
                                            value={crmNoteForm.followUpAt}
                                            onChange={(event) => setCrmNoteForm((current) => ({ ...current, followUpAt: event.target.value }))}
                                        />
                                    </div>
                                    <div className="venue-plan-upgrade-actions">
                                        <button type="button" className="manage-section-add-btn" onClick={handleAddCrmNote} disabled={isSaving}>
                                            <Mail size={16} />
                                            {isSaving ? 'Saving...' : 'Add Note'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="venue-plan-feature-panel venue-crm-timeline-panel">
                                <h3>CRM Timeline</h3>
                                {!crm?.notes?.length ? (
                                    <p>No CRM notes logged yet.</p>
                                ) : (
                                    <div className="venue-crm-note-list">
                                        {crm.notes.map((note) => (
                                            <article key={note.clubAdminCrmNoteId} className="venue-crm-note-card">
                                                <div className="venue-crm-note-meta">
                                                    <strong>{note.stageAtTime || 'Prospect'}</strong>
                                                    <span>{note.contactMethod || 'General note'}</span>
                                                    <span>{new Date(note.occurredAt).toLocaleString('en-GB')}</span>
                                                </div>
                                                <p>{note.note}</p>
                                                <div className="venue-crm-note-footer">
                                                    <span>Added by {note.createdByName || 'Shoot Connect Admin'}</span>
                                                    {note.followUpAt ? (
                                                        <span>Follow up: {new Date(note.followUpAt).toLocaleString('en-GB')}</span>
                                                    ) : null}
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="dashboard-card venue-plan-admin-card">
                            <h2>Shoot Connect Admin</h2>
                            <p>Use this toggle to control whether this venue has Premium venue access enabled in the platform.</p>
                            <div className="venue-plan-admin-switch-row">
                                <div className="venue-plan-admin-switch-copy">
                                    <strong>Premium venue access</strong>
                                    <span>
                                        {isPremiumVenue
                                            ? 'Premium venue features are currently enabled for this venue.'
                                            : 'Premium venue features are currently disabled for this venue.'}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className={`venue-toggle ${isPremiumVenue ? 'is-on' : ''}`}
                                    onClick={() => setIsPremiumVenue((currentValue) => !currentValue)}
                                    aria-pressed={isPremiumVenue}
                                    aria-label="Toggle premium venue access"
                                >
                                    <span className="venue-toggle-thumb" />
                                </button>
                            </div>
                            <div className="venue-plan-upgrade-actions">
                                <button type="button" className="manage-section-add-btn" onClick={handleAdminSave} disabled={isSaving}>
                                    <Save size={16} />
                                    {isSaving ? 'Saving...' : 'Save Premium Access'}
                                </button>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default VenuePlan;
